import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, LogOut, LayoutDashboard, Sun, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import timbernetLogoLight from "@/assets/timbernet-logo-light.png";
import timbernetLogoDark from "@/assets/timbernet-logo-dark.png";

const navLinks = [
  { to: "/", label: "Hem" },
  { to: "/skogstjanster", label: "Tjänster" },
  { to: "/annonser", label: "Annonser" },
  { to: "/saljare", label: "Medlemmar" },
  { to: "/skogsverktyg", label: "Verktyg" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { user, profile, signOut } = useAuth();
  const { theme } = useTheme();
  const logoSrc = theme === "dark" ? timbernetLogoDark : timbernetLogoLight;

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-card/80 backdrop-blur-lg">
      <div className="container flex h-20 items-center justify-between md:h-28">
        <Link to="/" className="flex items-center">
          <img src={logoSrc} alt="TimberNet logotyp" className="h-20 w-auto md:h-24" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 md:flex">
          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              state={l.to === "/annonser" ? { fresh: true } : undefined}
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-secondary ${
                location.pathname === l.to ? "bg-secondary text-primary" : "text-muted-foreground"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-2 md:flex">
          {user ? (
            <>
              <Button variant="outline" size="sm" asChild>
                <Link to="/dashboard">
                  <LayoutDashboard className="mr-2 h-4 w-4" />
                  {profile?.full_name || "Dashboard"}
                </Link>
              </Button>
              <Button variant="ghost" size="sm" onClick={signOut} aria-label="Logga ut">
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" asChild>
                <Link to="/logga-in">Logga in</Link>
              </Button>
              <Button size="sm" asChild>
                <Link to="/registrera">Registrera</Link>
              </Button>
            </>
          )}
        </div>

        {/* Mobile toggle */}
        <button className="md:hidden" onClick={() => setOpen(!open)} aria-label="Meny">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile menu overlay */}
      {open && (
        <div
          className="md:hidden fixed left-0 right-0 top-20 border-t border-border bg-card shadow-lg"
          style={{ zIndex: 1000 }}
        >
        <nav className="container flex flex-col gap-1 py-4">
          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              state={l.to === "/annonser" ? { fresh: true } : undefined}
              onClick={() => setOpen(false)}
              className={`rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                location.pathname === l.to ? "bg-secondary text-primary" : "text-muted-foreground"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <div className="mt-2 flex gap-2">
            {user ? (
              <>
                <Button variant="outline" size="sm" className="flex-1" asChild>
                  <Link to="/dashboard" onClick={() => setOpen(false)}>Dashboard</Link>
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { signOut(); setOpen(false); }} aria-label="Logga ut">
                  <LogOut className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" size="sm" className="flex-1" asChild>
                  <Link to="/logga-in" onClick={() => setOpen(false)}>Logga in</Link>
                </Button>
                <Button size="sm" className="flex-1" asChild>
                  <Link to="/registrera" onClick={() => setOpen(false)}>Registrera</Link>
                </Button>
              </>
            )}
          </div>
        </nav>
      </div>
      )}

      {/* Backdrop for closing on outside tap */}
      {open && (
        <div
          className="md:hidden fixed inset-0 top-20"
          style={{ zIndex: 999 }}
          onClick={() => setOpen(false)}
        />
      )}
    </header>
  );
}

export function Footer({ hideOnMobile = false }: { hideOnMobile?: boolean } = {}) {
  const { theme, toggleTheme } = useTheme();
  const logoSrc = theme === "dark" ? timbernetLogoDark : timbernetLogoLight;

  return (
    <footer className={`border-t border-border bg-card${hideOnMobile ? " hidden md:block" : ""}`}>
      <div className="container py-12">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="mb-3">
              <img src={logoSrc} alt="TimberNet logotyp" className="h-16 w-auto" />
            </div>
            <p className="text-sm text-muted-foreground">
              Sveriges digitala marknadsplats för skogstjänster och virke. Enkelt, tryggt och lokalt.
            </p>
          </div>
          <div>
            <h4 className="mb-3 font-sans text-sm font-semibold">Tjänster</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/skogstjanster" className="hover:text-primary transition-colors">Boka tjänst</Link></li>
              <li><Link to="/annonser" state={{ fresh: true }} className="hover:text-primary transition-colors">Köp ved & virke</Link></li>
              <li><Link to="/skogsverktyg" className="hover:text-primary transition-colors">Digitala verktyg</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="mb-3 font-sans text-sm font-semibold">Information</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/om-oss" className="hover:text-primary transition-colors">Om TimberNet</Link></li>
              <li><Link to="/villkor" className="hover:text-primary transition-colors">Villkor</Link></li>
              <li><Link to="/integritetspolicy" className="hover:text-primary transition-colors">Integritetspolicy</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="mb-3 font-sans text-sm font-semibold">Kontakt</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>info@timbernet.se</li>
              <li>08-123 456 78</li>
            </ul>
          </div>
        </div>
        <div className="mt-8 flex items-center justify-between border-t border-border pt-6">
          <span className="text-xs text-muted-foreground">
            © 2026 TimberNet. Alla rättigheter förbehållna.
          </span>
          <button
            onClick={toggleTheme}
            className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            aria-label={theme === "dark" ? "Byt till ljust läge" : "Byt till mörkt läge"}
          >
            {theme === "dark" ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
            <span className="hidden sm:inline">{theme === "dark" ? "Ljust läge" : "Mörkt läge"}</span>
          </button>
        </div>
      </div>
    </footer>
  );
}